import { call } from 'redux-saga/effects'
import qs from 'querystring'
import url from 'url'
import settings from '../../../../config/settings'
import { firebase, auth, functions } from '../../../utilities/firebase'
import getNormalisedParameters from '../../../utilities/getNormalisedParameters'
import { customError } from '../../../utilities/Errors/index'
import getAbsoluteUrlForCurrentHost from '../../../utilities/getAbsoluteUrlForCurrentHost'

export const getCustomClaims = async () => {
	const currentUser = auth.currentUser
	if (!currentUser) {
		return false
	}
	const idTokenResult = await currentUser.getIdTokenResult()
	return idTokenResult.claims
}

const loginWithPopup = async provider => auth.signInWithPopup(provider)
const loginWithRedirect = async provider => auth.signInWithRedirect(provider)

export const logout = async () => auth.signOut()
export const facebookLoginWithPopup = async () => loginWithPopup(new firebase.auth.FacebookAuthProvider())
export const facebookLoginWithRedirect = async () => loginWithRedirect(new firebase.auth.FacebookAuthProvider())
export const sendLoginLinkToEmail = async (email, actionCodeSettings) => auth.sendSignInLinkToEmail(email, actionCodeSettings)
export const isLoginWithEmailLink = async href => auth.isSignInWithEmailLink(href)
export const loginWithEmailLink = async (email, href) => auth.signInWithEmailLink(email, href)
export const getRedirectResult = async () => auth.getRedirectResult()
export const loginWithCustomToken = async token => auth.signInWithCustomToken(token)

// I am at a loss to explain why these (firebase.auth()) things need to be wrapped like this... is there something weird about the promise returned?
// I wanted to do e.g.:
//		yield call(auth.sendLoginLinkToEmail, email, actionCodeSettings)
// but it does not work, it errors with:
//		TypeError: Cannot read property 'N' of null

// NOTES:
//
// 1. Information on enabling IAM and adding permissions to the service account:
//    https://firebase.google.com/docs/auth/admin/create-custom-tokens#letting_the_admin_sdk_discover_a_service_account
//

export function* instagramLoginVerify(oauthData, redirectUrl) {
	if (!oauthData || oauthData.error) {
		throw customError('Instagram authorize error', oauthData)
	}

	// Verify token with provider, create firebase user, get token firebase token for user
	const { data: authInfo } = yield call(functions.httpsCallable('authInstagram'), { ...oauthData, redirectUrl })

	if (!authInfo || authInfo.error || !authInfo.token) {
		throw customError('Instagram token error', authInfo)
	}

	// Login to firebase
	const userCredential = yield call(loginWithCustomToken, authInfo.token)

	// Create and return the userInfo
	const userInfo = {
		...userCredential,
		additionalUserInfo: {
			...userCredential.additionalUserInfo,
			...authInfo.additionalUserInfo
		}
	}
	return userInfo
}

export function* instagramLoginWithPopup() {
	// Config
	const clientId = settings.instagram.clientId
	const redirectUrl = getAbsoluteUrlForCurrentHost('/auth/instagram/')
	const scope = 'basic'
	const authorisationUrlStub = 'https://api.instagram.com/oauth/authorize/'

	// Start popup auth
	const authorisationParams = {
		client_id: clientId,
		redirect_uri: redirectUrl,
		scope,
		response_type: 'code'
	}
	const authorisationUrl = `${authorisationUrlStub}?${qs.stringify(authorisationParams)}`
	const popup = global.open(authorisationUrl, '_blank')

	// Wait for token in redirect
	const oauthData = yield call(() => new Promise((resolve, reject) => {
		const parsedRedirectUrl = url.parse(redirectUrl)
		const redirectUri = `${parsedRedirectUrl.host}${parsedRedirectUrl.pathname}`
		const polling = setInterval(() => {
			if (!popup || popup.closed) {
				clearInterval(polling)
				reject(customError('Instagram authorize popup closed', { code: 'auth/popup-closed-by-user' }))
			}

			let popupUri
			try {
				popupUri = `${popup.location.host}${popup.location.pathname}`
			} catch (ex) {
				// swallow cross origin error (just waiting for location to come back us)
			}
			if (popupUri === redirectUri) {
				const data = getNormalisedParameters(popup.location.search)
				clearInterval(polling)
				popup.close()
				resolve(data)
			}
		}, 500)
	}))

	return yield call(instagramLoginVerify, oauthData, redirectUrl)
}

export function* instagramLoginWithRedirect() {
	// Config
	const clientId = settings.instagram.clientId
	const redirectUrl = getAbsoluteUrlForCurrentHost('/completeInstagramRedirectLogin')
	const scope = 'basic'
	const authorisationUrlStub = 'https://api.instagram.com/oauth/authorize/'

	const authorisationParams = {
		client_id: clientId,
		redirect_uri: redirectUrl,
		scope,
		response_type: 'code'
	}
	const authorisationUrl = `${authorisationUrlStub}?${qs.stringify(authorisationParams)}`
	yield global.location = authorisationUrl
}

export function* instagramLoginWithRedirectComplete() {
	// Config
	const redirectUrl = getAbsoluteUrlForCurrentHost('/completeInstagramRedirectLogin')

	const oauthData = getNormalisedParameters(global.location.search)

	return yield call(instagramLoginVerify, oauthData, redirectUrl)
}



// WEBPACK FOOTER //
// ./src/redux/saga/firebase/auth.js